/**
 * Owns local open/close state for the three modals rendered by the
 * external single-order layout (Approve EV-BV, Image viewer, Doc viewer).
 *
 * Also bridges local state to the underlying `risa-oasis-ui_v2` modal
 * registry via `openModal(...)` and to the redux modal slice.
 *
 * @module singleOrderForExternal/hooks/useExternalOrderModals
 */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { closeModal } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";

/**
 * @returns Modal open flags, openers / closers, and the redux `openedModalId`
 */
export const useExternalOrderModals = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useSelector((state: RootState) => state.modal);

  const [isApproveEvbvModalOpen, setIsApproveEvbvModalOpen] =
    useState<boolean>(false);
  const [isImageViewerModalOpen, setIsImageViewerModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (isApproveEvbvModalOpen) {
      openModal(ModalId.APPROVE_EVBV_MODAL);
    }
  }, [isApproveEvbvModalOpen]);

  useEffect(() => {
    if (isImageViewerModalOpen) {
      openModal(ModalId.IMAGE_VIEWER_MODAL);
    }
    if (openedModalId === ModalId.DOC_VIEWER_MODAL) {
      openModal(ModalId.DOC_VIEWER_MODAL);
    }
  }, [isImageViewerModalOpen, openedModalId]);

  return {
    openedModalId,
    isApproveEvbvModalOpen,
    openApproveEvbv: () => setIsApproveEvbvModalOpen(true),
    closeApproveEvbv: () => setIsApproveEvbvModalOpen(false),
    isImageViewerModalOpen,
    openImageViewer: () => setIsImageViewerModalOpen(true),
    closeImageViewer: () => setIsImageViewerModalOpen(false),
    closeDocViewer: () => dispatch(closeModal()),
  };
};
